// Estimator chat — front-end do chat com a Quali 🌈.
// POST /api/chat com { language, messages } e renderiza a resposta da IA.
// Em dev local (sem `vercel dev`), /api/chat retorna 404 — fallback gracioso
// para o usuário ir pro WhatsApp.

import { getLanguage, onLanguageChange, t } from "../lib/i18n";

type Sender = "ai" | "user";

type ChatMessage = {
  sender: Sender;
  text: string;
};

type ApiResponse = { text: string } | { error: string };

const MAX_USER_CHARS = 2000;
const MAX_MESSAGES = 20;
const API_ENDPOINT = "/api/chat";

export function initEstimatorChat(): void {
  const section = document.getElementById("estimator");
  if (!section) return;

  const messagesEl = section.querySelector<HTMLElement>("#chatMessages");
  const form = section.querySelector<HTMLFormElement>("#chatForm");
  const input = section.querySelector<HTMLInputElement>("#chatInput");
  const sendBtn = section.querySelector<HTMLButtonElement>("#chatSend");
  const suggestionButtons = section.querySelectorAll<HTMLButtonElement>(
    '.chat-suggestions button[data-prompt-key]',
  );

  if (!messagesEl || !form || !input || !sendBtn) return;

  // Histórico canônico — espelha os bubbles. Os 2 greets iniciais entram
  // como `ai` para a IA continuar de onde parou.
  const history: ChatMessage[] = [];
  let busy = false;

  function snapshotInitialGreets(): void {
    history.length = 0;
    const greets = messagesEl!.querySelectorAll<HTMLElement>(
      '.chat-bubble[data-chat-role="ai"]',
    );
    for (const g of greets) {
      const text = g.textContent?.trim();
      if (text) history.push({ sender: "ai", text });
    }
  }

  function setBusy(state: boolean): void {
    busy = state;
    input!.disabled = state;
    sendBtn!.disabled = state;
    for (const b of suggestionButtons) b.disabled = state;
  }

  function appendBubble(
    text: string,
    sender: Sender,
    opts: { error?: boolean } = {},
  ): HTMLElement {
    const bubble = document.createElement("div");
    bubble.className = "chat-bubble";
    if (sender === "user") bubble.classList.add("user");
    if (opts.error) bubble.classList.add("error");
    bubble.textContent = text;
    messagesEl!.appendChild(bubble);
    messagesEl!.scrollTop = messagesEl!.scrollHeight;
    return bubble;
  }

  function appendTyping(): HTMLElement {
    const bubble = document.createElement("div");
    bubble.className = "chat-bubble typing";
    bubble.setAttribute("aria-label", t("estimator.typing_aria"));
    bubble.setAttribute("role", "status");
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement("span");
      dot.setAttribute("aria-hidden", "true");
      bubble.appendChild(dot);
    }
    messagesEl!.appendChild(bubble);
    messagesEl!.scrollTop = messagesEl!.scrollHeight;
    return bubble;
  }

  function pushHistory(msg: ChatMessage): void {
    history.push(msg);
    // Trim histórico se exceder o limite (mantém os 2 greets + janela recente).
    if (history.length > MAX_MESSAGES) {
      const keepFromIdx = history.length - MAX_MESSAGES + 2;
      history.splice(2, keepFromIdx - 2);
    }
  }

  async function sendMessage(userText: string): Promise<void> {
    const trimmed = userText.trim();
    if (!trimmed || busy) return;

    if (trimmed.length > MAX_USER_CHARS) {
      appendBubble(t("estimator.error_validation"), "ai", { error: true });
      return;
    }

    appendBubble(trimmed, "user");
    pushHistory({ sender: "user", text: trimmed });
    input!.value = "";

    setBusy(true);
    const typing = appendTyping();

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          language: getLanguage(),
          messages: history,
        }),
      });

      typing.remove();

      if (response.status === 429) {
        appendBubble(t("estimator.error_rate"), "ai", { error: true });
        return;
      }

      if (response.status === 404) {
        // Dev local sem vercel — degrada gracioso.
        appendBubble(t("estimator.error_network"), "ai", { error: true });
        return;
      }

      const data = (await response.json().catch(() => null)) as ApiResponse | null;

      if (!response.ok || !data) {
        if (data && "error" in data && response.status === 413) {
          appendBubble(t("estimator.error_validation"), "ai", { error: true });
        } else {
          appendBubble(t("estimator.error_generic"), "ai", { error: true });
        }
        return;
      }

      if ("error" in data) {
        appendBubble(t("estimator.error_generic"), "ai", { error: true });
        return;
      }

      const aiText = data.text;
      appendBubble(aiText, "ai");
      pushHistory({ sender: "ai", text: aiText });
    } catch {
      typing.remove();
      appendBubble(t("estimator.error_network"), "ai", { error: true });
    } finally {
      setBusy(false);
      input!.focus();
    }
  }

  // Quando o usuário troca de idioma, os greets em DOM são re-traduzidos pelo
  // i18n-dom auto-apply. Re-snapshot do histórico inicial preserva o contrato
  // com a API.
  // Nota: NÃO faz reset — só se nenhuma mensagem foi enviada ainda.
  function maybeResnapshotGreets(): void {
    if (history.length <= 2) snapshotInitialGreets();
  }

  // Suggestion buttons → resolvem chave via t() para garantir versão traduzida.
  for (const btn of suggestionButtons) {
    btn.addEventListener("click", () => {
      const key = btn.dataset.promptKey;
      if (!key) return;
      const text = t(`estimator.${key}`);
      void sendMessage(text);
    });
  }

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void sendMessage(input.value);
  });

  // Sync com troca de idioma — `onLanguageChange` é o canal canônico
  // do projeto (lib/i18n.ts via i18next). bindI18nAutoApply roda primeiro
  // (registrado antes em main.ts), então o DOM já está com greets no
  // novo idioma quando este callback executa.
  onLanguageChange(() => {
    maybeResnapshotGreets();
  });

  snapshotInitialGreets();
}
